// ─── Yahoo Finance historical candle client ──────────────────────────────────
// Uses the /api/yahoo-chart serverless proxy (Vercel) or Vite dev proxy
// to fetch free historical daily closing prices from Yahoo Finance.
//
// Returns data in the same format as Finnhub's getCandles():
//   [{ date: 'YYYY-MM-DD', price: number }, ...]

const yahooCache = new Map();
const CACHE_TTL  = 300_000; // 5 min — same as Finnhub candle cache

// CBOE/Finnhub tickers → Yahoo Finance tickers for index data
const YAHOO_TICKER_MAP = {
  'SPX':  '^GSPC',
  'NDX':  '^NDX',
  'RUT':  '^RUT',
  'DJI':  '^DJI',
  'DJIA': '^DJI',
  'VIX':  '^VIX',
};

function toYahooTicker(ticker) {
  if (!ticker) return ticker;
  const upper = ticker.toUpperCase();
  return YAHOO_TICKER_MAP[upper] || ticker;
}

/** Clear the Yahoo candle cache (called by clearMarketCaches). */
export function clearYahooCache() { yahooCache.clear(); }

/**
 * Fetch daily closing prices for a ticker from Yahoo Finance.
 * @param {string} ticker  — e.g. 'AAPL', 'SPY'
 * @param {string} fromDate — 'YYYY-MM-DD'
 * @param {string} toDate   — 'YYYY-MM-DD'
 * @returns {Promise<Array<{date: string, price: number}>>}
 */
export async function getYahooCandles(ticker, fromDate, toDate) {
  const yahooSymbol = toYahooTicker(ticker);
  const cacheKey = `yf:${ticker}:${fromDate}:${toDate}`;
  const cached = yahooCache.get(cacheKey);
  if (cached && Date.now() - cached.ts < CACHE_TTL) return cached.data;

  const from = Math.floor(new Date(fromDate).getTime() / 1000);
  const to   = Math.floor(new Date(toDate).getTime()   / 1000);

  const res = await fetch(
    `/api/yahoo-chart?ticker=${encodeURIComponent(yahooSymbol)}&from=${from}&to=${to}`
  );

  if (!res.ok) {
    throw new Error(`Yahoo chart failed for ${ticker}: ${res.status}`);
  }

  const json = await res.json();
  const result = json.chart?.result?.[0];

  if (!result?.timestamp?.length) {
    // Yahoo returns an error object or empty result for unknown tickers
    const errMsg = json.chart?.error?.description;
    if (errMsg) throw new Error(`Yahoo: ${errMsg}`);
    return [];
  }

  const timestamps = result.timestamp;
  const closes     = result.indicators?.quote?.[0]?.close ?? [];

  const data = timestamps
    .map((ts, i) => {
      const d = new Date(ts * 1000);
      return {
        date:  isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10),
        price: closes[i],
      };
    })
    .filter(d => d.date && d.price != null); // filter out invalid dates and null prices

  yahooCache.set(cacheKey, { data, ts: Date.now() });
  return data;
}
